import React from 'react'

import { ListItemButton, Stack, Typography, useTheme } from '@mui/material'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'

import { PICKER_DROPDOWN, PICKER_SHEET } from './pickerListStyle'
import { MENU_ROW_LABEL_SX, menuRowToneSx } from './menuRowTone'

export interface PickerListItemProps {
  label: React.ReactNode
  /** Leading glyph — a flag emoji, an icon, etc. */
  leading?: React.ReactNode
  /** Trailing detail rendered before the selected check (e.g. a dial code). */
  trailing?: React.ReactNode
  selected?: boolean
  onClick: () => void
  /**
   * 'sheet' (default) = mobile bottom-sheet sizing — 16px text, an
   * explicit 24px line-height so the row clears the 44px touch-target
   * minimum. 'dropdown' = desktop floating-menu sizing (15px, dense),
   * matching `SortField` / `getPickerMenuProps()`. No current call site
   * needs 'dropdown' (every `PickerListItem` today lives in a bottom
   * sheet) — it's here so a future desktop list reuses this component
   * instead of hand-rolling a fourth picker row (working-conventions §36).
   */
  variant?: 'sheet' | 'dropdown'
}

/**
 * One row in a bottom-sheet picker list (country, gender, dial code…) — the
 * shape `SelectDrawer` and the Profile "Edit field" gender/country lists all
 * independently hand-rolled (three times over). Extracted per
 * working-conventions §36 ("extract a shared component on the SECOND
 * hand-roll") so item height, vertical gap, and font size come from one
 * place (`pickerListStyle.ts`) instead of drifting per call site.
 */
export const PickerListItem: React.FC<PickerListItemProps> = ({
  label,
  leading,
  trailing,
  selected = false,
  onClick,
  variant = 'sheet',
}) => {
  const theme = useTheme()
  const sizing = variant === 'sheet' ? PICKER_SHEET : PICKER_DROPDOWN

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        borderRadius: `${sizing.radius}px`,
        py: sizing.itemPY,
        px: sizing.itemPX,
        my: sizing.itemGap,
        /*
          The app's one menu-row tone (`menuRowTone.ts`): muted at rest, dark when
          selected, accent + grey wash on hover and keyboard focus.

          This row had NO hover rule of its own, so it fell through to MUI's
          `ListItemButton` default (`action.hover`, a flat 4% black) — which is
          why the "Activity type" list, the country picker and the profile
          gender/country lists all reacted differently from the Layout popover
          next door. `selectedBackground` because this row already carried
          `grey[100]` when selected and removing it here would be a redesign of
          three surfaces rather than the tone fix.
        */
        ...menuRowToneSx(theme, { selected, selectedBackground: true }),
      }}
    >
      <Stack direction='row' alignItems='center' gap={1.25} sx={{ minWidth: 0, flex: 1 }}>
        {leading}
        <Typography
          noWrap
          sx={{
            // Inherits the row, so it mutes at rest and takes the accent on hover.
            // Was pinned to `text.primary` in BOTH states, which left the check the
            // only thing distinguishing a selected row from an unselected one.
            ...MENU_ROW_LABEL_SX,
            fontSize: sizing.fontSize,
            ...(variant === 'sheet' && { lineHeight: PICKER_SHEET.lineHeight }),
          }}
        >
          {label}
        </Typography>
        {trailing && (
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontSize: sizing.fontSize,
              ml: 'auto',
              ...(variant === 'sheet' && { lineHeight: PICKER_SHEET.lineHeight }),
            }}
          >
            {trailing}
          </Typography>
        )}
      </Stack>
      {/* `currentColor`, so the tick travels with the row into the accent on
          hover. Pinned to `text.primary` it stayed black beside a label that had
          changed colour — the same defect the reference popover calls out. */}
      {selected && <CheckOutlinedIcon sx={{ color: 'currentColor', ml: 1, flexShrink: 0 }} />}
    </ListItemButton>
  )
}
